import { useEffect, useState } from "react";
import { Post } from "@/types/post";
import { fetchPost } from "@/utils/fetchPost";
import { cachePost, getCachedPost } from "@/utils/postCache";

export function usePost(postId: string | undefined) {
  const cached = getCachedPost(postId);
  const [post, setPost] = useState<Post | null>(cached ?? null);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (!postId) {
      setPost(null);
      setLoading(false);
      return;
    }

    // Cache hit — a producer already handed us this Post. Adopt it and skip
    // the fetch so PostDetailsScreen paints on the same frame as the
    // transition. We must always call setPost here, not just on first mount,
    // because expo-router can reuse this hook's host component across
    // navigations to different postIds — the initial useState would then
    // still hold the previous post. The feed's own refresh strategies
    // (refreshVisibleCounts on focus, etc.) freshen the cached copy over
    // time, so we deliberately don't chase it here.
    const hit = getCachedPost(postId);
    if (hit) {
      setPost(hit);
      setLoading(false);
      return;
    }

    // Cache miss on a fresh postId — clear stale content and show the
    // skeleton while fetching, so navigating from A to a not-yet-cached B
    // doesn't briefly display A.
    setPost(null);
    setLoading(true);

    let cancelled = false;
    (async () => {
      try {
        const result = await fetchPost(postId);
        if (cancelled) return;
        setPost(result);
        // Warm the cache from the URL-entry path too, so navigating away and
        // back doesn't re-fetch the same doc.
        if (result) cachePost(result);
      } catch (e) {
        if (cancelled) return;
        // Rules can deny reads (e.g. a public group was flipped to private
        // and the viewer is no longer a member). Fall through to the
        // POST_NOT_FOUND screen instead of an uncaught rejection.
        console.warn("Failed to load post", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [postId]);

  return { post, loading };
}
